import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PasswordSetForm } from '../components/PasswordSetForm';
import * as portalPasswordService from '../services/portalPasswordService';

interface SetPasswordPageProps {
	/**
	 * Same component covers both flows because the API path is identical:
	 * a one-time token plus a new password. Only the wording differs.
	 */
	mode: 'set-initial' | 'reset';
}

/**
 * Landing page for both the welcome (set initial password) and forgot-
 * password reset flows. The token comes from the email link as a query
 * param; we validate it up front so an expired link redirects the user
 * back to forgot-password rather than letting them fill out the form
 * and discover the failure on submit.
 */
export function SetPasswordPage({ mode }: SetPasswordPageProps): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token') ?? '';

	const [tokenStatus, setTokenStatus] = useState<
		'checking' | 'valid' | 'invalid'
	>('checking');
	const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (token.length === 0) {
				setTokenStatus('invalid');
				return;
			}
			try {
				const result = await portalPasswordService.validateResetToken(token);
				if (cancelled) return;
				setTokenStatus(result.valid ? 'valid' : 'invalid');
				setMaskedEmail(result.email ?? null);
			} catch {
				if (cancelled) return;
				setTokenStatus('invalid');
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [token]);

	const handleSubmit = async (values: {
		newPassword: string;
	}): Promise<void> => {
		setSubmitting(true);
		setSubmitError(null);
		try {
			await portalPasswordService.resetPassword(token, values.newPassword);
			setDone(true);
		} catch (error) {
			const message =
				error instanceof Error ? error.message : 'Unable to set password';
			setSubmitError(message);
		} finally {
			setSubmitting(false);
		}
	};

	if (tokenStatus === 'checking') {
		return (
			<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
				<p className="text-sm text-gray-600 dark:text-gray-400">
					{t('Verifying your link...')}
				</p>
			</div>
		);
	}

	if (tokenStatus === 'invalid') {
		return (
			<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
				<div className="w-full max-w-md bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
						{t('Link expired')}
					</h1>
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{t(
							'This link is no longer valid. Request a new one and we will email you a fresh link.',
						)}
					</p>
					<Link
						to="/forgot-password"
						className="inline-block rounded-md bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 transition-colors"
					>
						{t('Request a new link')}
					</Link>
				</div>
			</div>
		);
	}

	if (done) {
		return (
			<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
				<div className="w-full max-w-md bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
					<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
						{t('Password updated')}
					</h1>
					<p className="text-sm text-gray-600 dark:text-gray-400">
						{t('You can now sign in with your new password.')}
					</p>
					<button
						type="button"
						onClick={() => navigate('/')}
						className="inline-block rounded-md bg-primary-600 hover:bg-primary-700 text-white font-medium px-4 py-2 transition-colors"
					>
						{t('Sign in')}
					</button>
				</div>
			</div>
		);
	}

	const title = mode === 'set-initial' ? 'Set your password' : 'Reset password';
	const submitLabel =
		mode === 'set-initial' ? 'Set password' : 'Update password';
	const subtitle =
		maskedEmail != null
			? `For ${maskedEmail}. Choose a strong password to protect your account.`
			: 'Choose a strong password to protect your account.';

	return (
		<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
			<div className="w-full max-w-md bg-brand-surface border border-brand-border rounded-xl p-6">
				<PasswordSetForm
					title={title}
					subtitle={subtitle}
					submitLabel={submitLabel}
					onSubmit={handleSubmit}
					errorMessage={submitError}
					isSubmitting={submitting}
				/>
			</div>
		</div>
	);
}
